import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper,
  Textarea,
  Title,
  Divider,
  NumberInput,
  Alert, Modal, Text, Stack, Group, Button, Tooltip, LoadingOverlay, Anchor
} from '@mantine/core';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconArrowLeft } from '@tabler/icons-react';
import moment from 'moment';
import { useSalon } from '../../context/SalonContext';
import { useApp } from '../../context/AppContext';
import PaymentProcessingModal from './PaymentProcessingModal';
import { PaymentSplitButton } from './PaymentSplitButton';

function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apptId = searchParams.get('appt_id');
  const { businessInfo } = useSalon();
  const { getAppointment, updateAppointment, createPaymentIntent, updatePaymentIntent } = useApp();
  const [appointment, setAppointment] = useState(null);
  const [bookedServices, setBookedServices] = useState([]);
  const [adjustedPrice, setAdjustedPrice] = useState(0);
  const [priceAdjustmentReason, setPriceAdjustmentReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOverlay, setIsLoadingOverlay] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(10);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedNonCardPayment, setSelectedNonCardPayment] = useState('Cash');

  const NON_CARD_PAYMENT_OPTIONS = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Zelle', label: 'Zelle' },
    { value: 'CashApp', label: 'CashApp' },
    { value: 'Venmo', label: 'Venmo' }
  ];

  useEffect(() => {
    // Blocks the page from loading if the business does not have a stripe terminal device
    if (businessInfo && !businessInfo.stripe_terminal_device_id) {
      navigate('/admin/calendar');
      return null;
    }
  }, [businessInfo]);

  useEffect(() => {
    const fetchAppointment = async () => {
      const response = await getAppointment(apptId);
      console.log(response);
      setAppointment(response.appointment);
      setBookedServices(response.booked_services);
      setIsLoading(false);
    };
    fetchAppointment();
  }, [apptId]);

  useEffect(() => {
    if (bookedServices.length > 0) {
      setAdjustedPrice(bookedServices[0]?.charged_price);
    }
  }, [bookedServices]);

  const handleBackToCalendar = () => {
    navigate('/admin/calendar');
  };

  const handleUpdatePaymentIntent = async () => {
    const response = await updatePaymentIntent({
      paymentIntentId: businessInfo.stripe_payment_intent_id,
      amount: adjustedPrice
    });
    return response;
  }

  const convertAndFormatDateTime = (dateTime) => {
    if (!dateTime) return 'Unknown date/time';
    const date = moment.utc(dateTime);
    const startEST = date.tz(businessInfo?.timezone);
    return `${startEST.format('MMM DD, YYYY h:mm A')}`;
  }

  const handleCreatePaymentIntent = async () => {
    const startEST = convertAndFormatDateTime(appointment?.start_dt);
    const response = await createPaymentIntent({
      amount: adjustedPrice,
      appointmentId: apptId,
      salonId: businessInfo.id,
      accountId: businessInfo.stripe_connect_account_id,
      description: `Payment for ${appointment?.first_name} ${appointment?.last_name} on ${startEST} for ${bookedServices[0]?.name} with ${appointment?.primary_staff_name}`
    });
    return response;
  }
  

  const handleCardPayment = async () => {
    setIsLoadingOverlay(true);
    try {
      const response = businessInfo.stripe_payment_intent_id 
        ? await handleUpdatePaymentIntent() 
        : await handleCreatePaymentIntent();

      if (response?.paymentIntentId) {
        setAppointment({
          ...appointment,
          stripe_payment_intent_id: response.paymentIntentId
        });
        setShowPaymentModal(true);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
    } finally {
      setIsLoadingOverlay(false);
    }
  };

  const handleNonCardPayment = async () => {
    setIsLoading(true);
    try {
      const priceAdjustmentNote = priceAdjustmentReason ? `Price Adjustment Reason: ${priceAdjustmentReason}` : '';
      const response = await updateAppointment({
        id: apptId, 
        status: 'Completed', 
        final_charged_price: adjustedPrice,
        payment_method: selectedNonCardPayment,
        salon_notes: appointment?.salon_notes ? appointment.salon_notes + ` | ${priceAdjustmentNote}` : priceAdjustmentNote
      });
      
      if (response) {
        setShowSuccessAlert(true);
        setCountdownSeconds(10);

        const countdownInterval = setInterval(() => {
          setCountdownSeconds((prev) => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              navigate('/admin/calendar');
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNonCardPaymentClick = () => {
    setShowConfirmModal(true);
  };

  const formatAppointmentDatetime = (start, end) => {
    if (!start || !end) return '';

    const startMoment = moment.utc(start);
    const endMoment = moment.utc(end);
    const startEST = startMoment.tz('America/New_York');
    const endEST = endMoment.tz('America/New_York');

    if (startEST.isSame(endEST, 'day')) {
      return `${startEST.format('MMM DD, YYYY • h:mm A')} - ${endEST.format('h:mm A')}`;
    }
    
    return `${startEST.format('MMM DD, YYYY • h:mm A')}-${endEST.format('MMM, DD, YYYY • h:mm A')}`;
  }

  return isLoading ? (
    <LoadingOverlay visible={isLoading} />
  ) : appointment?.stripe_payment_intent_status === 'succeeded' ? (
    <Text size="sm" py="xl" ta="center">Payment already recorded. Click <Anchor href="/admin/calendar">here</Anchor> to return to the calendar.</Text>
  ) : (
    <>
      <LoadingOverlay visible={isLoadingOverlay} />
      {showSuccessAlert && (
        <Alert
          color="green"
          styles={{
            root: {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              borderRadius: 0
            }
          }}
        >
          Payment Recorded. Returning to calendar in {countdownSeconds} seconds...
        </Alert>
      )}
      <Modal
        opened={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={`Confirm ${selectedNonCardPayment} Payment`}
        centered
      >
        <Stack spacing="md">
          <Text>Please confirm that you have received {selectedNonCardPayment} payment of ${adjustedPrice} from {appointment?.first_name} {appointment?.last_name}.</Text>
          <Text>Once confirmed, the appointment will be marked as completed and payment will be recorded.</Text>
          <Group justify="space-between">
            <Button 
              variant="subtle" 
              color="red"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button 
              color="green"
              onClick={() => {
                setShowConfirmModal(false);
                handleNonCardPayment();
              }}
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>
      <PaymentProcessingModal
        opened={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        paymentIntentId={appointment?.stripe_payment_intent_id}
        apptId={apptId}
        customerEmail={appointment?.email}
        priceAdjustmentReason={priceAdjustmentReason}
        appointment={appointment}
      />
      <Container size="sm" py="xl">
        <Paper shadow="sm" p="md" withBorder>
          <Stack gap="xl">
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={handleBackToCalendar}
              style={{ alignSelf: 'flex-start' }}
            >
              Back to Appointment Calendar
            </Button>

            <Stack gap="xs" align="center">
              <Title order={3} ta="center">Checking out {appointment?.first_name} {appointment?.last_name}</Title>
              <Text size="lg" c="dimmed" ta="center">
                {bookedServices[0]?.name}
                {appointment?.primary_staff_name ? ` (serviced by ${appointment?.primary_staff_name})` : ''}
              </Text>
              <Text size="md" c="dimmed">{formatAppointmentDatetime(appointment?.start_dt, appointment?.end_dt)}</Text>
            </Stack>


            {/* Notes Section */}
            {(appointment?.customer_notes || appointment?.salon_notes) && (
              <>
                <Divider size="xs" w={400} mx="auto" />
                <Stack spacing="md">
                  <Title order={4}>Notes</Title>
                  {appointment?.customer_notes && (
                    <Stack spacing="xs">
                      <Text weight={500}>Customer Notes:</Text>
                      <Text>{appointment.customer_notes}</Text>
                    </Stack>
                  )}
                  {appointment?.salon_notes && (
                    <Stack spacing="xs">
                      <Text weight={500}>Salon Notes:</Text>
                      <Text>{appointment.salon_notes}</Text>
                    </Stack>
                  )}
                </Stack>
              </>
            )}

            <Divider w="100%" mx="auto" style={{ maxWidth: '400px' }} />

            <Stack gap="md" align="center">
              <Title order={3}>Payment Details</Title>
              <Paper withBorder p="md" bg="gray.0" w="100%" style={{ maxWidth: '380px' }}>
                <Stack gap="xs" align="center">
                    <Group justify="space-between" w="100%">
                    <Text>Listed Service Price:</Text>
                    <Text>
                        ${bookedServices[0]?.charged_price}
                        {bookedServices[0]?.is_variable_price && (
                        <Text span c="dimmed" ml={4}>and up</Text>
                        )}
                    </Text>
                    </Group>
                    <Group justify="space-between" w="100%">
                        <Text>Final Price (excl. tip)</Text>
                        <Tooltip
                            label="Enter the final amount to charge the customer"
                            position="top-start"
                            multiline
                            width={220}
                        >
                            <NumberInput
                                value={adjustedPrice}
                                onChange={(value) => setAdjustedPrice(value)}
                                decimalScale={2}
                                fixedDecimalScale
                                min={0}
                                w={130}
                            />
                        </Tooltip>
                    </Group>
                </Stack>
              </Paper>


              {adjustedPrice !== bookedServices[0]?.charged_price && (
                <Textarea
                  label="Reason for Price Adjustment (Optional)"
                  value={priceAdjustmentReason}
                  onChange={(event) => setPriceAdjustmentReason(event.currentTarget.value)}
                  placeholder="Enter reason for price adjustment..."
                />
              )}
            </Stack>

            <Divider w="100%" mx="auto" style={{ maxWidth: '400px' }} />

            {/* Payment Method Selection */}
            <Stack gap="md" align="center" w="100%" style={{ maxWidth: '400px' }} mx="auto">
              <Title order={3}>Payment Method</Title>
              <Group grow w="100%">
                <Button 
                  variant="outline" 
                  color="blue" 
                  onClick={() => handleCardPayment()}
                  size="lg"
                >
                  Card
                </Button>
                <PaymentSplitButton
                  options={NON_CARD_PAYMENT_OPTIONS}
                  value={selectedNonCardPayment}
                  onChange={(value) => {
                    setSelectedNonCardPayment(value);
                    handleNonCardPaymentClick();
                  }}
                  onSelect={(value) => {
                    setSelectedNonCardPayment(value);
                    handleNonCardPaymentClick();
                  }}
                  color="green"
                />
              </Group>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    </>
  )
}

export default CheckoutPage; 